import React, { FunctionComponent, useEffect } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import ReactTooltip from "react-tooltip";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { loadAgencyMetrics } from "../../actions/dashboardActions";
import WidgetLoading from "../shared/loaders/WidgetLoading";
import agencyMetricsType from "../AgencyPerformanceMetrics/types/agencyMetricsType";
import {
  Wrapper,
  Header,
  Body,
  Row,
  Column,
  Number,
  Description,
  InfoDot,
} from "./styles/agency-pipeline-metrics-styles";
import { formatCurrencyWithSymbol } from "../../helpers/formatters";

interface IAgencyPipelineMetricsTypes {
  pipeline: {
    interviews: number;
    interviewsCount: number;
    engagements: number;
    engagementsCount: number;
    currency: {
      symbol: string;
      isoCode: string;
    };
  };
}

interface IAgencyPipelineMetricsProps {
  loadAgencyMetrics: (args: { recruiterId: number }) => void;
  loading: boolean;
  agencyMetrics: IAgencyPipelineMetricsTypes;
  recruiterId: number;
}

export const AgencyPipelineMetrics: FunctionComponent<
  IAgencyPipelineMetricsProps
> = ({ loadAgencyMetrics, loading, agencyMetrics, recruiterId }) => {
  useEffect(() => {
    loadAgencyMetrics({ recruiterId });
  }, []);

  const interviewsTooltip =
    "Potential earnings from your <br /> current engagements at interview";
  const engagementsTooltip =
    "Potential earnings from your <br /> current active engagements";

  const currencySymbol = agencyMetrics
    ? agencyMetrics.pipeline.currency.symbol
    : "";

  return (
    <Wrapper>
      {loading && <WidgetLoading zIndex={1} />}
      <Header className="panel-heading">
        <div className="panel-title">Estimated Pipeline</div>
      </Header>
      <Body>
        <ReactTooltip
          html
          place="bottom"
          type="dark"
          effect="float"
          aria-haspopup="true"
          id="pipeline-tooltip"
        />
        <Row>
          <Column
            flex={1}
            data-tip={interviewsTooltip}
            data-for="pipeline-tooltip"
          >
            <Number fontSize="large" data-test-id="interviews">
              {agencyMetrics
                ? formatCurrencyWithSymbol({
                    symbol: currencySymbol,
                    value: agencyMetrics.pipeline?.interviews ?? 0
                  })
                : "£0"}
              <InfoDot>
                <FontAwesomeIcon icon={faInfoCircle} />
              </InfoDot>
            </Number>
            <Description data-test-id="interviews-count">
              From{" "}
              {agencyMetrics &&
                Math.round(agencyMetrics.pipeline.interviewsCount)}{" "}
              Engagements at Interview
            </Description>
          </Column>
          <Column
            flex={1}
            data-tip={engagementsTooltip}
            data-for="pipeline-tooltip"
          >
            <Number fontSize="large" data-test-id="engagements">
              {agencyMetrics
                ? formatCurrencyWithSymbol({
                    symbol: currencySymbol,
                    value: agencyMetrics.pipeline?.engagements ?? 0
                  })
                : "£0"}
              <InfoDot>
                <FontAwesomeIcon icon={faInfoCircle} />
              </InfoDot>
            </Number>
            <Description data-test-id="engagements-count">
              From {agencyMetrics && agencyMetrics.pipeline.engagementsCount}{" "}
              Engagements
            </Description>
          </Column>
        </Row>
      </Body>
    </Wrapper>
  );
};

AgencyPipelineMetrics.propTypes = {
  loadAgencyMetrics: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  agencyMetrics: agencyMetricsType,
  recruiterId: PropTypes.number.isRequired,
};

AgencyPipelineMetrics.defaultProps = {
  agencyMetrics: null,
};

interface IReduxState {
  dashboard: {
    loading: boolean;
    agencyMetrics: IAgencyPipelineMetricsTypes;
  };
}

const mapStateToProps = (state: IReduxState) => ({
  loading: state.dashboard.loading,
  agencyMetrics: state.dashboard.agencyMetrics,
});

export default connect(mapStateToProps, { loadAgencyMetrics })(
  AgencyPipelineMetrics
);
